/**
 * Mic button + inline error for voice notes (#3829).
 *
 * Idle: a `Mic` button. Recording: a stop button with a running `m:ss` timer.
 * Transcribing: a spinner. Renders nothing when voice notes are unavailable
 * (switched off server-side, or the browser cannot record). Agency-neutral
 * copy -- never a vendor name.
 */
import { Loader2, Mic, Square } from "lucide-react";
import { formatElapsed, type VoiceNote } from "./useVoiceNote";

interface ButtonProps {
  voice: VoiceNote;
  disabled?: boolean;
  className?: string;
}

const BASE =
  "inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center gap-1.5 rounded-lg border transition-colors";

export function VoiceNoteButton({ voice, disabled = false, className = "" }: ButtonProps) {
  if (!voice.available) return null;

  if (voice.phase === "recording") {
    const time = formatElapsed(voice.elapsed);
    return (
      <button
        type="button"
        onClick={voice.stop}
        aria-label={`Stop recording (${time})`}
        title="Stop recording"
        data-testid="voice-note-stop"
        className={`${BASE} border-[var(--color-danger)] px-2.5 text-sm font-medium tabular-nums text-[var(--color-danger)] hover:bg-[var(--color-bg-page)] ${className}`}
      >
        <Square className="h-4 w-4 fill-current" aria-hidden="true" />
        <span aria-hidden="true">{time}</span>
      </button>
    );
  }

  if (voice.phase === "transcribing") {
    return (
      <button
        type="button"
        disabled
        aria-label="Turning your voice note into text"
        data-testid="voice-note-busy"
        className={`${BASE} cursor-wait border-[var(--color-border)] text-[var(--color-text-muted)] ${className}`}
      >
        <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => void voice.start()}
      disabled={disabled}
      aria-label="Record a voice note"
      title="Voice note"
      data-testid="voice-note-start"
      className={`${BASE} border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-page)] hover:text-[var(--color-accent)] disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    >
      <Mic className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}

/** The plain-language error line (and a live "recording" cue for screen readers). */
export function VoiceNoteStatus({ voice }: { voice: VoiceNote }) {
  if (!voice.available) return null;
  return (
    <>
      <span className="sr-only" aria-live="polite">
        {voice.phase === "recording"
          ? "Recording. Press stop when you're done."
          : voice.phase === "transcribing"
            ? "Turning your voice note into text."
            : ""}
      </span>
      {voice.error && (
        <p role="alert" className="text-sm text-[var(--color-danger)]" data-testid="voice-note-error">
          {voice.error}
        </p>
      )}
    </>
  );
}
